import { useState, useEffect } from 'react';
import axios from 'axios';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { TrendingDown, Info } from 'lucide-react';
import { API } from '../App';

import { parseUtc } from '../lib/parseUtc';
const PERIODS = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
];

export default function ShadowCostGraph({ clusterId }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clusterId) return;
    setLoading(true);
    axios
      .get(`${API}/clusters/${clusterId}/shadow-cost?days=${days}`)
      .then(r => setData(r.data))
      .catch(e => console.error('Shadow cost fetch failed:', e))
      .finally(() => setLoading(false));
  }, [clusterId, days]);

  const timeline = data?.timeline || [];
  const summary = data?.summary || {};

  // Downsample to max 100 points, always include the latest data point
  const chartData = (() => {
    if (timeline.length <= 100) return timeline;
    const step = Math.ceil(timeline.length / 100);
    const sampled = timeline.filter((_, i) => i % step === 0);
    if (sampled[sampled.length - 1] !== timeline[timeline.length - 1]) {
      sampled.push(timeline[timeline.length - 1]);
    }
    return sampled;
  })();

  if (loading) {
    return (
      <div className="h-[400px] w-full bg-slate-900/50 rounded-2xl animate-pulse flex items-center justify-center border border-white/5">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-slate-500 font-medium text-sm">Loading Shadow Simulation...</span>
        </div>
      </div>
    );
  }

  if (!chartData.length) {
    return (
      <Card className="border border-white/5 bg-slate-900/50 shadow-2xl h-[400px] flex items-center justify-center">
        <div className="text-slate-500 text-center">
          <Info className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>No shadow simulation data available yet</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="border shadow-lg overflow-hidden relative group">
      <div className="absolute inset-0 bg-gradient-to-b from-blue-50/50 to-transparent pointer-events-none" />

      <CardHeader className="border-b pb-4 relative z-10">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-blue-500/20">
                <TrendingDown className="w-4 h-4 text-blue-400" />
              </div>
              Cost: With vs Without OptOps
            </CardTitle>
            <p className="text-xs text-slate-400 mt-1 ml-1">Shadow simulation — on-demand baseline vs actual</p>
          </div>

          <div className="flex items-center gap-4 text-xs font-medium">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-blue-500 ring-2 ring-blue-500/20"></span>
              <span className="text-slate-600">Without OptOps</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-emerald-500/20 animate-pulse"></span>
              <span className="text-emerald-400">With OptOps</span>
            </div>
            <div className="flex gap-1 ml-2">
              {PERIODS.map(p => (
                <button
                  key={p.days}
                  onClick={() => setDays(p.days)}
                  className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                    days === p.days
                      ? 'bg-blue-500/20 text-blue-400'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-6 relative z-10">
        {/* Summary row */}
        {summary.total_savings > 0 && (
          <div className="flex gap-6 mb-4 text-xs font-medium">
            <span className="text-slate-500">Without OptOps: <span className="text-blue-400">${summary.total_shadow?.toFixed(2)}</span></span>
            <span className="text-slate-500">With OptOps: <span className="text-emerald-400">${summary.total_actual?.toFixed(2)}</span></span>
            <span className="text-slate-500">Saved: <span className="text-emerald-400">${summary.total_savings?.toFixed(2)} ({summary.avg_savings_pct}%)</span></span>
          </div>
        )}

        <div className="h-[350px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="shadowBaselineGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.4} />
                  <stop offset="50%" stopColor="#3b82f6" stopOpacity={0.1} />
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.0} />
                </linearGradient>
                <linearGradient id="shadowActualGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.5} />
                  <stop offset="50%" stopColor="#10b981" stopOpacity={0.2} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0.0} />
                </linearGradient>
              </defs>

              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />

              <XAxis
                dataKey="timestamp"
                stroke="#475569"
                fontSize={10}
                tickLine={false}
                axisLine={false}
                tickFormatter={v => new Date(v).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              />

              <YAxis
                stroke="#475569"
                fontSize={10}
                tickLine={false}
                axisLine={false}
                tickFormatter={v => `$${v}`}
              />

              <Tooltip
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    const d = payload[0]?.payload;
                    return (
                      <div className="bg-white/95 border border-slate-200 p-3 rounded-xl shadow-xl backdrop-blur-md">
                        <p className="text-slate-500 text-xs mb-2">{parseUtc(d.timestamp).toLocaleDateString()}</p>
                        <div className="flex items-center justify-between gap-4 mb-1">
                          <span className="text-xs font-medium text-slate-600 flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                            Without OptOps
                          </span>
                          <span className="text-sm font-bold font-mono text-blue-600">${d.shadow_cost?.toFixed(2)}/day</span>
                        </div>
                        <div className="flex items-center justify-between gap-4 mb-1">
                          <span className="text-xs font-medium text-slate-600 flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                            With OptOps
                          </span>
                          <span className="text-sm font-bold font-mono text-emerald-600">${d.actual_cost?.toFixed(2)}/day</span>
                        </div>
                        <div className="border-t border-slate-200 mt-1 pt-1 flex items-center justify-between gap-4">
                          <span className="text-xs font-medium text-slate-600">Saved</span>
                          <span className="text-sm font-bold font-mono text-emerald-600">${d.savings?.toFixed(2)} ({d.savings_pct}%)</span>
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />

              <Area
                type="monotone"
                dataKey="shadow_cost"
                name="Without OptOps"
                stroke="#3b82f6"
                strokeWidth={2}
                fill="url(#shadowBaselineGrad)"
                animationDuration={1500}
              />

              <Area
                type="monotone"
                dataKey="actual_cost"
                name="With OptOps"
                stroke="#10b981"
                strokeWidth={2}
                strokeDasharray="5 5"
                fill="url(#shadowActualGrad)"
                animationDuration={2000}
                animationEasing="ease-out"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
